import { useState } from 'react'
import {
  useKbList,
  useKbConfig,
  useCreateKb,
  useUpdateKb,
  useDeleteKb,
  useReindexKb,
} from '@/hooks/useKnowledgeBases'
import { DataTable, type ColumnDef } from '@/components/common/DataTable'
import { StatusBadge } from '@/components/common/StatusBadge'
import { ConfirmDialog } from '@/components/admin/ConfirmDialog'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { KnowledgeBase } from '@/types'
import { formatDateTime, getApiErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'
import { Plus, Pencil, Trash2, RotateCw } from 'lucide-react'

const XLSX_EXTENSION = /\.xlsx$/i

interface CreateForm {
  file: File | null
  name: string
  description: string
  keywords: string
}

interface EditForm {
  name: string
  description: string
  keywords: string
}

const emptyCreateForm: CreateForm = { file: null, name: '', description: '', keywords: '' }

function splitKeywords(raw: string): string[] {
  return raw
    .split(',')
    .map((k) => k.trim())
    .filter((k) => k.length > 0)
}

export const KnowledgeBasesPage = () => {
  const { data: kbs, isLoading } = useKbList()
  const { data: config } = useKbConfig()
  const createKb = useCreateKb()
  const updateKb = useUpdateKb()
  const deleteKb = useDeleteKb()
  const reindexKb = useReindexKb()

  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<CreateForm>({ ...emptyCreateForm })
  const [editKb, setEditKb] = useState<KnowledgeBase | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({ name: '', description: '', keywords: '' })
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeBase | null>(null)

  const maxFileMb = config?.limits.max_file_mb ?? 10

  const openCreate = () => {
    setCreateForm({ ...emptyCreateForm })
    setCreateOpen(true)
  }

  const openEdit = (kb: KnowledgeBase) => {
    setEditForm({
      name: kb.name,
      description: kb.description ?? '',
      keywords: kb.keywords.join(', '),
    })
    setEditKb(kb)
  }

  const handleCreate = async () => {
    if (!createForm.file || !createForm.name) return

    if (!XLSX_EXTENSION.test(createForm.file.name)) {
      toast.error('El archivo debe ser un Excel (.xlsx)')
      return
    }

    const sizeMb = createForm.file.size / (1024 * 1024)
    if (sizeMb > maxFileMb) {
      toast.error(`El archivo supera el tamaño máximo permitido (${maxFileMb} MB)`)
      return
    }

    try {
      await createKb.mutateAsync({
        file: createForm.file,
        name: createForm.name,
        description: createForm.description || undefined,
        keywords: splitKeywords(createForm.keywords),
      })
      setCreateOpen(false)
    } catch {
      // Dialog stays open on failure; useCreateKb's onError already
      // surfaces the error toast — avoid showing it twice.
    }
  }

  const handleEditSave = async () => {
    if (!editKb) return
    try {
      await updateKb.mutateAsync({
        kbId: editKb.kb_id,
        updates: {
          name: editForm.name,
          description: editForm.description,
          keywords: splitKeywords(editForm.keywords),
        },
      })
      setEditKb(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al actualizar base de conocimiento'))
    }
  }

  const columns: ColumnDef<KnowledgeBase>[] = [
    {
      key: 'name',
      header: 'Nombre',
      cell: (kb) => (
        <div>
          <p className="font-medium">{kb.name}</p>
          <p className="text-xs text-muted-foreground">{kb.original_filename}</p>
          {kb.status === 'error' && kb.error && (
            <p className="mt-1 text-xs text-destructive">{kb.error}</p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (kb) => <StatusBadge status={kb.status} />,
    },
    {
      key: 'chunk_count',
      header: 'Fragmentos',
      cell: (kb) => <span className="font-medium">{kb.chunk_count}</span>,
    },
    {
      key: 'keywords',
      header: 'Palabras clave',
      cell: (kb) => (
        <div className="flex flex-wrap gap-1">
          {kb.keywords.length === 0 ? (
            <span className="text-xs text-muted-foreground">Ninguna</span>
          ) : (
            kb.keywords.map((kw) => (
              <Badge key={kw} variant="outline" className="text-xs">
                {kw}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      key: 'updated_at',
      header: 'Actualizado',
      cell: (kb) => (
        <span className="text-sm text-muted-foreground">
          {kb.updated_at ? formatDateTime(kb.updated_at) : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (kb) => (
        <div className="flex items-center justify-end gap-2">
          {kb.status === 'error' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => reindexKb.mutate(kb.kb_id)}
              disabled={reindexKb.isPending}
              title="Reintentar indexación"
            >
              <RotateCw className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => openEdit(kb)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(kb)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Bases de conocimiento</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Crear base de conocimiento
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={columns}
            data={kbs ?? []}
            isLoading={isLoading}
            keyExtractor={(kb) => kb.kb_id}
            emptyMessage="No hay bases de conocimiento del sistema"
          />
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear base de conocimiento</DialogTitle>
            <DialogDescription>
              Sube un archivo Excel (.xlsx) para usarlo como fuente de contexto en el chat. Tamaño
              máximo: {maxFileMb} MB.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Archivo (.xlsx)</Label>
              <Input
                type="file"
                accept=".xlsx"
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, file: e.target.files?.[0] ?? null }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input
                placeholder="Muertes violentas 2025"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Descripción</Label>
              <Input
                placeholder="Descripción de la base de conocimiento..."
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Palabras clave (separadas por coma)</Label>
              <Input
                placeholder="seguridad, homicidios, quito"
                value={createForm.keywords}
                onChange={(e) => setCreateForm((f) => ({ ...f, keywords: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createKb.isPending || !createForm.file || !createForm.name}
            >
              {createKb.isPending ? 'Subiendo...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editKb} onOpenChange={(open) => !open && setEditKb(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar base de conocimiento: {editKb?.name}</DialogTitle>
            <DialogDescription>
              Cambiar nombre, descripción o palabras clave no vuelve a procesar el archivo.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Descripción</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Palabras clave (separadas por coma)</Label>
              <Input
                value={editForm.keywords}
                onChange={(e) => setEditForm((f) => ({ ...f, keywords: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditKb(null)}>
              Cancelar
            </Button>
            <Button onClick={handleEditSave} disabled={updateKb.isPending || !editForm.name}>
              {updateKb.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      {deleteTarget && (
        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          title="Eliminar base de conocimiento"
          description={`¿Eliminar "${deleteTarget.name}"? Se eliminará el archivo y todos sus fragmentos indexados. Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          variant="destructive"
          onConfirm={() => deleteKb.mutateAsync(deleteTarget.kb_id)}
          isLoading={deleteKb.isPending}
        />
      )}
    </div>
  )
}
